import React from "react";
import { captureError } from "../monitoring";

/*
 * ErrorBoundary — last-resort catch for React render / lifecycle errors.
 *
 * Plain class component (React 18 has no hook-based equivalent). Any
 * uncaught throw below this boundary flips state.hasError, rendering a
 * recovery screen instead of a blank page. The error is forwarded to
 * Sentry via the existing monitoring helper, with enough React context
 * (component stack + the boundary's `name`) to locate the origin.
 *
 * Usage:
 *   <ErrorBoundary name="root"><App /></ErrorBoundary>
 *
 * Optional props:
 *   - name:     string tag attached to the Sentry report
 *   - onReset:  called when the user clicks "Try again"
 *   - fallback: node | (({ error, reset }) => node) for a custom UI
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    try {
      captureError(error, {
        boundary: this.props.name || "unnamed",
        componentStack: info?.componentStack,
      });
    } catch {
      /* Never let a monitoring failure mask the original error. */
    }
  }

  reset() {
    this.setState({ hasError: false, error: null });
    try { this.props.onReset?.(); } catch {}
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") {
      return fallback({ error: this.state.error, reset: this.reset });
    }
    if (fallback) return fallback;

    const msg = this.state.error?.message || "Something went wrong.";
    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#FAFAF9",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          color: "#1A1714",
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: "100%",
            background: "#FFFFFF",
            border: "1px solid #E9E4DC",
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 4px 24px rgba(0,0,0,.06)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 34, marginBottom: 12 }} aria-hidden="true">⚠️</div>
          <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p style={{ margin: "0 0 24px", fontSize: 14, lineHeight: 1.55, color: "#555" }}>
            The app hit an unexpected error. Your data is safe — reloading usually
            clears it. If this keeps happening, our team has already been notified.
          </p>
          {isDev && (
            <pre
              style={{
                textAlign: "left",
                fontSize: 12,
                background: "#F5F4F2",
                border: "1px solid #E9E4DC",
                borderRadius: 8,
                padding: 12,
                margin: "0 0 20px",
                overflow: "auto",
                maxHeight: 160,
                color: "#933",
              }}
            >
              {msg}
            </pre>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              type="button"
              onClick={this.reset}
              style={{
                appearance: "none",
                border: "1px solid #1A1714",
                background: "#1A1714",
                color: "#FFFFFF",
                padding: "10px 18px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                try { window.location.reload(); } catch {}
              }}
              style={{
                appearance: "none",
                border: "1px solid #E9E4DC",
                background: "#FFFFFF",
                color: "#1A1714",
                padding: "10px 18px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
